import { AfterViewInit, Component, effect, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, PLATFORM_ID, Renderer2, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

export type MessageType = 'error' | 'success' | 'warning';

@Component({
  selector: 'app-error-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './error-modal.component.html',
  styleUrl: './error-modal.component.scss',
})
export class ErrorModalComponent implements AfterViewInit, OnDestroy {
  @Input() isOpen = signal(false);
  @Input() type: MessageType = 'error';
  @Input() title = '';
  @Input() buttonLabel = '';
  @Input() message: string | string[] = '';
  @Output() close = new EventEmitter<void>();
  @Output() send = new EventEmitter<void>();

  get messages(): string[] {
    return Array.isArray(this.message) ? this.message : [this.message];
  }

  get iconName(): string {
    switch (this.type) {
      case 'error':
        return 'error';
      case 'success':
        return 'check_circle';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  }

  get colorClasses(): { bg: string; text: string; border: string } {
    switch (this.type) {
      case 'error':
        return {
          bg: 'bg-red-50',
          text: 'text-red-500',
          border: 'border-red-200',
        };
      case 'success':
        return {
          bg: 'bg-secondary/50',
          text: 'text-secondary',
          border: 'border-secondary/50',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          text: 'text-yellow-600',
          border: 'border-yellow-200',
        };
      default:
        return {
          bg: 'bg-blue-50',
          text: 'text-blue-600',
          border: 'border-blue-200',
        };
    }
  }

  private modalElement: HTMLElement | null = null;
  private platformId = inject(PLATFORM_ID);

  private static zIndexCounter = 9990;
  private static openErrorModalsCounter = 0;
  zIndex = 9990;
  
  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {
    // Watch for isOpen changes and handle body overflow
    effect((onCleanup) => {
      if (!isPlatformBrowser(this.platformId)) return;
      
      if (this.isOpen()) {
        ErrorModalComponent.zIndexCounter += 10;
        this.zIndex = ErrorModalComponent.zIndexCounter;

        if (this.modalElement) {
          this.renderer.appendChild(document.body, this.modalElement);
        }

        ErrorModalComponent.openErrorModalsCounter++;
        // Add a specialized class for error modal to not conflict directly with form-modal's inline style
        this.renderer.setStyle(document.body, 'overflow', 'hidden');

        const keydownListener = this.renderer.listen('document', 'keydown', (event: KeyboardEvent) => {
          if (event.key === 'Escape') this.onClose();
        });

        onCleanup(() => {
          keydownListener();
          ErrorModalComponent.openErrorModalsCounter = Math.max(0, ErrorModalComponent.openErrorModalsCounter - 1);
          // Only remove if it was the last error modal. NOTE: If form-modals are open, they might lose `overflow: hidden` here.
          // Ideally, a global service manages body scroll, but we avoid breaking existing logic.
          if (ErrorModalComponent.openErrorModalsCounter === 0) {
             // We can tentatively remove it, but check if there's a `.form-modal-open` class? We didn't add one.
             // We'll proceed with standard removal as it was before.
            this.renderer.removeStyle(document.body, 'overflow');
          }
        });
      }
    });
  }

  ngAfterViewInit(): void {
    // Move modal to body to escape parent container constraints
    if (isPlatformBrowser(this.platformId)) {
      this.modalElement = this.elementRef.nativeElement;
      if (this.modalElement) {
        this.renderer.appendChild(document.body, this.modalElement);
      }
    }
  }

  ngOnDestroy(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Clean up: remove modal from body
    if (this.modalElement && this.modalElement.parentNode) {
      this.renderer.removeChild(document.body, this.modalElement);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onSend(): void {
    this.send.emit();
  }
}
